import { useCallback, useEffect, useState } from 'react';
import { LocationParams } from '../components/search/admin-dashboard-tables/interfaces';
import fetchSearchParams from '../utils/fetchSearchParams';

const useRequestLocation = (waitTime: number) => {
  const fetchInitialSearchLocation = () => {
    const { lat, lng }: LocationParams = fetchSearchParams();
    if (lat && lng) {
      return `${lat}, ${lng}`;
    }
    if (localStorage.getItem('latitude') && localStorage.getItem('longitude')) {
      return `${localStorage.getItem('latitude')}, ${localStorage.getItem('longitude')}`;
    }
    return '';
  };

  const fetchInitialUseSearchByLocation = () => {
    const { lat, lng }: LocationParams = fetchSearchParams();
    return !!(lat && lng);
  };

  const [searchLocation, setSearchLocation] = useState(fetchInitialSearchLocation());
  const [showRequestLocation, setShowRequestLocation] = useState(false);
  const [isAuto, setIsAuto] = useState(false);
  const [locationIsLoading, setLocationIsLoading] = useState(false);
  const [useSearchByLocation, setUseSearchByLocation] = useState(fetchInitialUseSearchByLocation());

  const requestLocation = (location: string) => {
    if (!location && !sessionStorage.getItem('disable-popup-location')) {
      setIsAuto(true);
      setShowRequestLocation(true);
    }
  };

  const closeModal = useCallback(() => {
    if (isAuto) {
      setIsAuto(false);
    }
    setShowRequestLocation(false);
  }, [isAuto]);

  const updateLocation = useCallback((e: any) => {
    const { detail } = e as { detail: { newLocation: boolean } };
    if (detail && detail.newLocation) {
      if (localStorage.getItem('latitude') && localStorage.getItem('longitude')) {
        const newLocation = `${localStorage.getItem('latitude')}, ${localStorage.getItem('longitude')}`;
        if (newLocation !== searchLocation) {
          setSearchLocation(newLocation);
        }
      }
    }
  }, [searchLocation]);

  useEffect(() => {
    document.addEventListener('updateLocation', updateLocation);
    return () => document.removeEventListener('updateLocation', updateLocation);
  }, [updateLocation]);

  useEffect(() => {
    const timer = setTimeout(
      () => requestLocation(searchLocation),
      waitTime,
    );
    return () => clearTimeout(timer);
  }, [searchLocation, waitTime]);

  const checkLocation = useCallback(() => {
    if (!useSearchByLocation) {
      if (searchLocation) {
        setUseSearchByLocation(true);
      } else {
        setShowRequestLocation(true);
      }
    } else {
      setUseSearchByLocation(false);
    }
  }, [searchLocation, useSearchByLocation]);

  const toggleSearchByLocation = useCallback((on: boolean) => {
    if (on && !useSearchByLocation) {
      if (searchLocation) {
        setUseSearchByLocation(true);
      } else {
        setShowRequestLocation(true);
      }
    } else {
      setUseSearchByLocation(false);
    }
  }, [searchLocation, useSearchByLocation]);

  const stopAsking = () => {
    sessionStorage.setItem('disable-popup-location', 'disabled');
    closeModal();
  };

  const onCompletionCallback = (toggleLocationSearchOn = false) => {
    closeModal();
    setLocationIsLoading(false);
    setSearchLocation(`${localStorage.getItem('latitude')}, ${localStorage.getItem('longitude')}`);
    if (toggleLocationSearchOn) {
      setUseSearchByLocation(true);
    }
  };

  return {
    searchLocation,
    showRequestLocation,
    useSearchByLocation,
    checkLocation,
    closeModal,
    locationIsLoading,
    setLocationIsLoading,
    stopAsking,
    isAuto,
    onCompletionCallback,
    toggleSearchByLocation,
  };
};

export default useRequestLocation;
